import styled from "@emotion/styled";
import { useEffect, useRef, useState } from "react";
import UI from "@mile-meeting/mile-ui";
import { TReservation } from "apis/meetings/meeting-reservation";
import MeetingRoomInfoModal from "../booking-list/component/meeting-room-info-modal";
import { useRecoilValue } from "recoil";
import { member } from "recoil/recoilState";
import { getDay, getKorDate } from "utils/date";
import { extractHtmlText } from "utils/validation";
import { UtilsService } from "utils/utils.service";
import { RRuleService } from "utils/rrule.service";
import { isInTimeRange } from "./my-scheduled-rsv";
import { useToast } from "hook/useToast";

type RsvDetailInfoSideModalProps = {
  open?: boolean;
  setOpen?: (value: boolean) => void;
  canEdit?: boolean;
  onHandleEdit?: (reservation?: TReservation) => void;
  onHandleDelete?: (reservation?: TReservation) => void;
  reservation?: TReservation;
};

const RsvDetailInfoSideModal = ({
  open,
  setOpen,
  canEdit = false,
  onHandleEdit,
  onHandleDelete,
  reservation,
}: RsvDetailInfoSideModalProps) => {
  const utilsService = new UtilsService();
  const rruleService = new RRuleService(utilsService);
  const [title, setTitle] = useState<string>("");
  const { showToast } = useToast();

  const memberInfo = useRecoilValue(member);
  const ref = useRef<HTMLDivElement>(null);
  const [openInfo, setOpenInfo] = useState<boolean>(false);
  const meetingRoomInfoRef = useRef<HTMLDivElement>(null);

  const meetingRoom = reservation?.meetingRoom?.name ?? "-";
  const startTime = reservation?.startTime ?? "";
  const endTime = reservation?.endTime ?? "";
  const isMyReservation = reservation?.reservationCreator?.member?.id === memberInfo?.id;
  const isVisitorReservation = reservation?.reservationCreator?.isVisitor ?? false;

  const terminateDate = reservation?.rrule ? rruleService.getPeriodOfRecurrence(reservation?.rrule).terminatedAt : null;

  useEffect(() => {
    setTitle(reservation?.title ?? "");
  }, [reservation]);

  const formatTimeRange = (start: string, end: string): string => {
    const startDate = new Date(start);
    const endDate = new Date(end);

    const pad = (num: number): string => num.toString().padStart(2, "0");
    const startTime = `${pad(startDate.getHours())}:${pad(startDate.getMinutes())}`;
    const endTime = `${pad(endDate.getHours())}:${pad(endDate.getMinutes())}`;

    return `${startTime} - ${endTime}`;
  };

  return (
    <Container ref={ref} style={{ right: open ? 0 : "-480px" }}>
      <div style={{ position: "absolute", top: 20, right: 20, cursor: "pointer" }}>
        <UI.IconButton.Normal
          type="primary"
          icon="x"
          size={20}
          color="var(--alias-color-label-normal)"
          onClick={() => setOpen?.(false)}
        />
      </div>
      <ContentContainer>
        <UI.Typography type="title03" fontStyle="bold" color="var(--alias-color-label-neutral)">
          예약 상세 정보
        </UI.Typography>
        <InfoContainer className="info-container">
          <div style={{ display: "flex", gap: "8px", alignItems: "flex-start", width: "100%" }}>
            <UI.Typography
              type="heading02"
              fontStyle="medium"
              color="var(--alias-color-label-normal)"
              style={{ width: "100%", whiteSpace: "normal" }}
            >
              {title}
            </UI.Typography>
            {new Date(reservation?.endTime ?? "") < new Date() && reservation?.rsvStatus !== "DONE" && (
              <UI.Tag label="예약 불참" icon="removeCircle" type="primary" color="pink" showIcon />
            )}
            {reservation?.rsvStatus === "DONE" && (
              <UI.Tag label="이용 완료" icon="checkCircle" type="primary" color="cyan" showIcon />
            )}
          </div>
          <div className="line" />
          <BasicInfoContainer>
            <div className="info">
              <div className="label">
                <UI.Icon name="meetingRoom2" size={16} color="var(--alias-color-label-assistive)" />
                <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
                  회의실
                </UI.Typography>
              </div>
              <div className="content">
                <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-normal)">
                  {meetingRoom}
                </UI.Typography>
                <div
                  style={{ position: "relative", cursor: "pointer", display: "flex" }}
                  onFocus={() => setOpenInfo(true)}
                  tabIndex={0}
                  ref={meetingRoomInfoRef}
                >
                  <UI.Icon name="info" size={20} color="var(--alias-color-label-assistive)" />
                  <MeetingRoomInfoModal
                    setOpen={setOpenInfo}
                    open={openInfo}
                    position={{
                      top: 24,
                      left:
                        (ref.current?.getBoundingClientRect()?.right ?? 0) -
                          (meetingRoomInfoRef.current?.getBoundingClientRect()?.right ?? 0) >
                        180
                          ? 0
                          : -180,
                    }}
                    meetingRoom={
                      reservation?.meetingRoom ?? {
                        id: "",
                        name: "",
                        capacity: 0,
                        description: "",
                        isInsideTabletUsed: false,
                        isOutsideTabletUsed: false,
                        isBlocked: false,
                        building: {
                          id: "",
                          name: "",
                          provider: "MILE",
                        },
                        floor: {
                          id: "",
                          name: "",
                        },
                        image: "",
                        meetingRoomIntegration: {
                          id: "",
                          integrationProvider: "MILE",
                          resourceName: "",
                          capacity: 0,
                          description: "",
                          privateMeetingDataCollected: false,
                        },
                      }
                    }
                  />
                </div>
              </div>
            </div>
            <div className="info">
              <div className="label">
                <UI.Icon name="calendar" size={16} color="var(--alias-color-label-assistive)" />
                <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
                  예약 일자
                </UI.Typography>
              </div>
              <div className="content">
                <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-normal)">
                  {getKorDate(startTime)} ({getDay(startTime)})
                </UI.Typography>
              </div>
            </div>
            <div className="info">
              <div className="label">
                <UI.Icon name="clock" size={16} color="var(--alias-color-label-assistive)" />
                <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
                  예약 시간
                </UI.Typography>
              </div>
              <div className="content">
                <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-normal)">
                  {formatTimeRange(startTime, endTime)}
                </UI.Typography>
              </div>
            </div>
            {reservation?.isRecurring && (
              <div className="info">
                <div className="label">
                  <UI.Icon name="change" size={16} color="var(--alias-color-label-assistive)" />
                  <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
                    반복 조건
                  </UI.Typography>
                </div>
                <div className="content">
                  <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-normal)">
                    {reservation?.rrule ? rruleService.transRRule(reservation?.rrule) : ""} |{" "}
                    {terminateDate
                      ? `${getKorDate(terminateDate.toString()).replaceAll(".", ". ")} 반복 종료`
                      : "무기한 반복"}
                  </UI.Typography>
                </div>
              </div>
            )}
          </BasicInfoContainer>
          <div className="line" />
          <PeopleInfoContainer>
            <div className="info">
              <div className="label">
                <UI.Icon name="person" size={16} color="var(--alias-color-label-assistive)" />
                <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
                  예약자
                </UI.Typography>
              </div>
              {isVisitorReservation ? (
                <UI.Tag
                  type="primary"
                  color="gray"
                  label={reservation?.reservationCreator?.visitorEmail ?? "-"}
                  icon="person"
                  showIcon
                />
              ) : (
                <TagContainer primary={isMyReservation}>
                  {reservation?.reservationCreator?.member?.image ? (
                    <img src={reservation?.reservationCreator.member?.image} alt="primary-tag" />
                  ) : (
                    <UI.Icon
                      name="person"
                      size={20}
                      color={
                        reservation?.reservationCreator?.member?.id === memberInfo?.id
                          ? "white"
                          : "var(--alias-color-primary-normal)"
                      }
                    />
                  )}
                  <UI.Typography
                    type="label01"
                    fontStyle="medium"
                    color={isMyReservation ? "white" : "var(--alias-color-primary-normal)"}
                  >
                    {reservation?.reservationCreator?.member?.nickname ?? "-"}
                  </UI.Typography>
                </TagContainer>
              )}
            </div>
            <div className="info">
              <div className="label">
                <UI.Icon name="people" size={16} color="var(--alias-color-label-assistive)" />
                <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
                  참석자
                </UI.Typography>
              </div>
              <div className="attendees">
                {reservation?.reservationAttendees?.map((attendee) => {
                  if (attendee.isVisitor) {
                    return <UI.Tag type="primary" color="gray" label={attendee.visitorEmail} icon="person" showIcon />;
                  } else {
                    return (
                      <TagContainer primary={attendee.member.id === memberInfo?.id}>
                        {attendee.member.image ? (
                          <img src={attendee.member.image} alt="profile" />
                        ) : (
                          <UI.Icon
                            name="person"
                            size={20}
                            color={
                              attendee.member.id === memberInfo?.id ? "white" : "var(--alias-color-primary-normal)"
                            }
                          />
                        )}
                        <UI.Typography
                          type="label01"
                          fontStyle="medium"
                          color={attendee.member.id === memberInfo?.id ? "white" : "var(--alias-color-primary-normal)"}
                        >
                          {attendee.member?.nickname ?? "-"}
                        </UI.Typography>
                      </TagContainer>
                    );
                  }
                })}
              </div>
            </div>
          </PeopleInfoContainer>
          <div className="line" />
          <NoteContainer>
            <div className="label">
              <UI.Icon name="eyeClosed" size={16} color="var(--alias-color-label-assistive)" />
              <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
                비공개 여부
              </UI.Typography>
            </div>
            <UI.Typography
              type="body01 normal"
              fontStyle="regular"
              color="var(--alias-color-label-normal)"
              style={{
                whiteSpace: "pre-wrap",
              }}
            >
              {reservation?.isPrivate ? "비공개" : "공개"}
            </UI.Typography>
          </NoteContainer>
          <NoteContainer>
            <div className="label">
              <UI.Icon name="notes" size={16} color="var(--alias-color-label-assistive)" />
              <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
                예약 메모
              </UI.Typography>
            </div>
            <UI.Typography
              type="body01 normal"
              fontStyle="regular"
              color="var(--alias-color-label-normal)"
              style={{
                whiteSpace: "pre-wrap",
              }}
            >
              {extractHtmlText(reservation?.memo).text ?? "-"}
            </UI.Typography>
          </NoteContainer>
        </InfoContainer>
      </ContentContainer>
      {canEdit && (
        <BtnContainer>
          <UI.Button
            title="수정하기"
            disabled={!isInTimeRange(reservation?.startTime ?? "", reservation?.endTime ?? "")}
            primary={false}
            prefixIcon="modify"
            showPrefixIcon
            onClick={() => {
              if (!isInTimeRange(reservation?.startTime ?? "", reservation?.endTime ?? "")) {
                showToast("07:00 - 23:00 이외의 시간대 예약은 수정을 지원하지 않습니다.", "warning");
                return;
              }
              onHandleEdit?.(reservation);
            }}
          />
          <UI.Button
            type="outlined"
            title="삭제하기"
            primary={false}
            prefixIcon="trashCan"
            showPrefixIcon
            onClick={() => onHandleDelete?.(reservation)}
          />
        </BtnContainer>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  box-sizing: border-box;
  height: calc(100% - 64px);
  flex-direction: column;
  align-items: flex-start;
  gap: 40px;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 24px 40px 40px 40px;
  width: 480px;

  position: fixed;
  right: 0px;
  top: 64px;
  z-index: 5;

  transition: all ease 0.25s;
  white-space: nowrap;

  background: var(--alias-color-background-normal-normal);
  box-shadow: var(--alias-style-shadow-heavy);
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 32px;
  flex: 1 0 0;
  align-self: stretch;
`;

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  flex: 1 0 0;
  align-self: stretch;

  .line {
    height: 1px;
    width: 100%;
    background: var(--alias-color-line-normal);
  }
`;

const BasicInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  align-self: stretch;
  white-space: pre-wrap;

  .info {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    gap: 4px;
    align-self: stretch;
  }

  .label {
    display: flex;
    align-items: center;
    gap: 4px;
  }
  .content {
    display: flex;
    align-items: flex-start;
    gap: 4px;
    align-items: center;
  }
`;

const PeopleInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  align-self: stretch;

  .info {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    align-self: stretch;
  }
  .label {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .attendees {
    display: flex;
    align-items: flex-start;
    align-content: flex-start;
    gap: 8px 8px;
    align-self: stretch;
    flex-wrap: wrap;
  }
`;

const TagContainer = styled.div<{ primary: boolean }>`
  display: flex;
  padding: 4px 12px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  background-color: ${(props) => (props.primary ? "var(--alias-color-primary-normal)" : "var(--global-color-blue-95)")};
  border-radius: 40px;

  img {
    width: 20px;
    height: 20px;
    background-color: ${(props) => (props.primary ? "white" : "var(--alias-color-primary-normal)")};
    border-radius: 40px;
    object-fit: cover;
  }
`;

const NoteContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;

  .label {
    display: flex;
    align-items: center;
    gap: 4px;
  }
`;

const BtnContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  align-self: stretch;
`;
export default RsvDetailInfoSideModal;
